/* Core & libraries */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Skeleton from "react-loading-skeleton";

/* Helpers, constants, custom methods, internal libraries */
import { useUserContext } from "../../contexts/userContext";
import { useLoyaltyService } from "../../services/loyaltyService";

/* Components */
import UCButton from "../buttons/index.js";
import { ReactComponent as LoyaltyWelcomeIcon } from "../../resources/images/loyalty-welcome.svg";
import LoyaltyTermsModal from "../../components/modals/loyaltyTermsModal";

/* Styles */
import styles from "./styles.js";
import useStorageState from "../../hooks/useStorageState";
import classNames from "classnames";

const LoyaltyContent = ({ showLoyaltyConsent, onTermsAccepted, showContent, setTermsAccepted }) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const userContext = useUserContext();
  const { getLoyaltyTermsAccepted, loyaltyAcceptedResponse } = useLoyaltyService();

  const [showLoyaltyTermsModal, _setShowLoyaltyTermsModal] = useState(false);
  const [lepTermsAccepted, setLepTermsAccepted] = useState(null); 
  const [loading, setLoading] = useState(true);   
  const hasFetched = useRef(false); 

  const companyEntityId = userContext?.userInfo?.companyInfo?.companyEntityId;
  const lepCanAcceptTerms = userContext?.userInfo?.lepCanAcceptTerms;

  const setShowLoyaltyTermsModal = (value, terms) => {
    _setShowLoyaltyTermsModal(value);
  }

  const showModal = useCallback(() => {
    setShowLoyaltyTermsModal(true);
  }, []);

  useEffect(() => {
    if (companyEntityId && !hasFetched.current) {
      hasFetched.current = true;
      getLoyaltyTermsAccepted(companyEntityId).finally(() => setLoading(false)); 
    }
  }, [companyEntityId, getLoyaltyTermsAccepted]);

  useEffect(() => {
    if (loyaltyAcceptedResponse !== undefined) {
      setLepTermsAccepted(loyaltyAcceptedResponse);
    }
  }, [loyaltyAcceptedResponse]);

  useEffect(() => {
    if (loyaltyAcceptedResponse !== showLoyaltyConsent) {
      setLepTermsAccepted(showLoyaltyConsent);
    }
  }, [showLoyaltyConsent]);

  return (
    <div className={classes.loyaltyPageContent}>
      {loading ? (
        <Container className={classes.container}>
          {!lepCanAcceptTerms && <Row className={classes.outerContainer}>
            <Col className={classes.contentContainer} xs={12}>
              <Skeleton height={40} width={150} />
            </Col>
          </Row>}
        </Container>
      ) : showContent && lepCanAcceptTerms && !lepTermsAccepted ? (
        <Container className={classes.container}>
          <Row className={classes.outerContainer}>
            <Col className={classes.contentContainer} xs={12}>
              <span className={classes.loyaltyWelcomeIconContainer}>
                <LoyaltyWelcomeIcon />
              </span>
              <p className={classes.welomeTextTitle}><FormattedMessage id="loyalty.welcomeTextTitle" /></p>
              <p className={classes.welomeTextDescription}><FormattedMessage id="loyalty.welcomeTextDescription" /></p>
              <UCButton
                className={classes.button}
                type="default"
                onClick={showModal}
                text={<FormattedMessage id="loyalty.acceptTermsButtonText" />}
                spinner={{
                  name: "updateCompanySpinner",
                  size: 20,
                }}
              />
            </Col>
          </Row>
        </Container>
      ) : (
        <UCButton
          type="general"
          className={classNames(classes.button, classes.resubmitButton)}
          onClick={showModal}
          text={<FormattedMessage id="loyalty.resubmitTermsAndConditions" />}
          textClassName={classes.resubmitButtonContainer}
        />
      )}

      <LoyaltyTermsModal
        showModal={showLoyaltyTermsModal}
        setShowModal={setShowLoyaltyTermsModal}
        onTermsAccepted={onTermsAccepted}
        setTermsAccepted={setTermsAccepted}
      />
    </div>
  );
};

export default LoyaltyContent;